import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { ApiResponse, Identifiable, TableId } from "types/models.ts";

import ExportResponse = App.Http.Responses.ExportResponse;

interface UseTableDataApiOperationsOptions {
    additionalDependencies?: unknown[];
    shouldFetch?: boolean;
}

export const useTableDataApiOperations = <R, U = void, T extends Identifiable = object, E = object>(
    tableId: TableId,
    fetchQuery: UseQueryResult<R, unknown>,
    deleteMutation?: UseMutationResult<void, unknown, T>,
    updateMutation?: UseMutationResult<U, unknown, T>,
    exportMutation?: UseMutationResult<ExportResponse, unknown, E>,
    options?: UseTableDataApiOperationsOptions
) => {
    const [tableData, setTableData] = useState<R | null>(null);
    const lastAutomaticFetchKey = useRef<string | null>(null);
    const tableAtomInstance = tableAtom<T>();
    const [tableState, setTableState] = useRecoilState(tableAtomInstance(tableId));

    const pageNumber = tableState.page?.number;
    const pageSize = tableState.page?.size;
    const filterObj = tableState.filter?.filter;
    const sortObj = tableState.filter?.sort;

    const fetchData = async () => {
        try {
            const response = await fetchQuery.refetch();
            if (response && response.data) {
                setTableData(response.data);
            }
        } catch (error) {
            console.log("refetch error", error);
        }
    };

    useEffect(() => {
        if (!tableState.initialized) return;
        if (options?.shouldFetch === false) return;
        if (pageNumber == null || pageSize == null) return;

        const fetchKey = JSON.stringify({
            tableId,
            pageNumber,
            pageSize,
            filterObj,
            sortObj,
            additionalDependencies: options?.additionalDependencies,
        });
        if (lastAutomaticFetchKey.current === fetchKey) return;
        lastAutomaticFetchKey.current = fetchKey;

        fetchData();
    }, [tableState.initialized, pageNumber, pageSize, filterObj, sortObj, ...(options?.additionalDependencies || [])]);

    const deleteTableData = async (record: T) => {
        deleteMutation?.mutate(record, {
            onSuccess: () => {
                toast.success(`Položka byla smazána.`);
                fetchData();
            },
            onError: (error) => {
                const axiosError = error as AxiosError<ApiResponse<{ error: string }>>;
                console.log(`Error deleting form object: ${axiosError.message}`);
            },
        });
    };

    const editTableData = async (record: T) => {
        updateMutation?.mutate(record, {
            onSuccess: () => {
                //toast.success(`${record.id} was updated.`);
                fetchData();
            },
            onError: (error) => {
                const axiosError = error as AxiosError<ApiResponse<{ error: string }>>;
                console.log(`Error updating form object: ${axiosError.message}`);
            },
        });
    };

    const exportData = async (request: E): Promise<string | undefined> => {
        try {
            const response = await exportMutation?.mutateAsync(request);
            return response?.url;
        } catch (err) {
            return undefined;
        }
    };

    return {
        tableData,
        tableState,
        setTableState,
        refetchData: fetchData,
        deleteTableData,
        editTableData,
        fetchQuery,
        exportData,
    };
};
